/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Reservation} from 'types/entities/Reservation'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import {SellerAssignSellerRequest, SellerDuplicateOfferRequest, SellerReservationRebookingRequest, SellerSetCustomerAsLeadRequest,} from 'types/entities/SellerReservations'
import ajax from 'helpers/ajax'
import {DEFAULT_HALL_ID, SELLER_API_KEY} from 'helpers/config'
import {consoleLog} from 'utils/utility'
import {PackagesCart} from 'types/entities/PackagesCart'
import {Company} from 'types/entities/Company'
import {Driver} from 'types/entities/Driver'
import SessionAction from 'store/session/SessionAction'
import {ReservationCheckoutPlan} from 'types/entities/ReservationCheckoutPlan'
import {ThunkAction} from 'model/store/thunk'
import {PackageUpsellItem} from 'store/booking/BookingAction'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {
	clearReservations,
	clearTableState,
	deleteReservation,
	deleteReservationNote,
	pushReservationNote,
	resetDefaultReservationsTable,
	setExtraInvoiceNoteToReservation,
	setExtraOfferNoteToReservation,
	setPackageCartToReservation,
	setReservation,
	setReservationPlans,
	setReservations,
	setReservationsTableUiState,
	setTableState,
} from 'store/seller/SellerReducer'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import {PackagesCartItemVoucher} from 'types/entities/PackagesCartItemVoucher'
import {ReservationPlan} from 'types/entities/ReservationPlan'
import type {CompanyBranchType} from "types/enums/CompanyBranchType";

type SellerReservationUpsellItem = PackageUpsellItem & {
    price?: number
    custom_text?: string
}

class SellerAction {
    // Re-export slice actions from SellerReducer
    static setReservations = setReservations
    static setTableState = setTableState
    static setReservationsTableUiState = setReservationsTableUiState
    static resetDefaultReservationsTable = resetDefaultReservationsTable
    static setReservation = setReservation
    static deleteReservation = deleteReservation
    static clearReservations = clearReservations
    static clearTableState = clearTableState
    static pushReservationNote = pushReservationNote
    static deleteReservationNote = deleteReservationNote
    static setPackageCartToReservation = setPackageCartToReservation
    static setReservationPlans = setReservationPlans
    static setExtraInvoiceNoteToReservation = setExtraInvoiceNoteToReservation
    static setExtraOfferNoteToReservation = setExtraOfferNoteToReservation

    static fetchGetSellerLoginWithPassword = (email: string, password: string): ((dispatch) => Promise<Driver>) => {
        return async (dispatch) => {
            try {
                const response: {
                    user: Driver
                    reason?: string
                    access_token: string
                    token_type: string
                    expires_at: number
                    uid: number
                    home_hall_id: number
                    watchdogWorking: boolean
                } = await ajax.post('/admin/seller/login-with-password', {
                    identifier: email,
                    password: password,
                    api_key: SELLER_API_KEY,
                })
                if (response.user) {
                    dispatch(SessionAction.setSession(response))
                    return Promise.resolve(response.user)
                } else {
                    consoleLog('fetchGetSellerLoginWithPassword - REJECT')
                    return Promise.reject(response.reason)
                }
            } catch (err) {
                return Promise.reject(err)
            }
        }
    }

    /**
     * @deprecated Wont be used on the on reservations
     * @param data
     */
    static fetchSetReservationRebooking = (data: SellerReservationRebookingRequest) => {
        return (dispatch, getState) => {
            return ajax
                .post(
                    '/online-venue/reservation/seller/rebooking/' + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        date: data?.date,
                        time: data?.time,
                        api_key: SELLER_API_KEY,
                    },
                    {}
                )
                .then(
                    (onResolve: { reservation: Reservation }) => {
                        return Promise.resolve(onResolve)
                    },
                    (onReject) => {
                        consoleLog('Fetch data error:', onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        consoleLog('Request aborted:', error.message)
                    } else {
                        consoleLog('Request error:', error)
                    }
                    return Promise.reject(error)
                })
        }
    }

    /**
     * @deprecated Wont be used on the on reservations
     * @param data
     */
    static fetchAssignSellerToReservation = (data: SellerAssignSellerRequest) => {
        return (dispatch, getState) => {
            return ajax
                .post(
                    '/online-venue/reservation/seller/assign-to-reservation/' + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        seller_id: data.seller_id,
                    },
                    {}
                )
                .then(
                    (onResolve: { reservation: Reservation }) => {
                        return Promise.resolve(onResolve)
                    },
                    (onReject) => {
                        consoleLog('Fetch data error:', onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        consoleLog('Request aborted:', error.message)
                    } else {
                        consoleLog('Request error:', error)
                    }
                    return Promise.reject(error)
                })
        }
    }

    /***
     * @deprecated Use offers actions instead
     */
    static fetchDuplicateOffer = (data: SellerDuplicateOfferRequest) => {
        return (dispatch, getState) => {
            return ajax
                .post(
                    '/online-venue/reservation/seller/duplicate/' + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        option: data?.option,
                    },
                    {}
                )
                .then(
                    (onResolve: { success: boolean }) => {
                        return Promise.resolve(onResolve)
                    },
                    (onReject) => {
                        consoleLog('Fetch data error:', onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        consoleLog('Request aborted:', error.message)
                    } else {
                        consoleLog('Request error:', error)
                    }
                    return Promise.reject(error)
                })
        }
    }

    /**
     * @deprecated Is not available on API anymore
     * @param data
     * @param packagesCart
     */
    static fetchSetCustomerAsLead = (data: SellerSetCustomerAsLeadRequest, packagesCart: PackagesCart) => {
        return (dispatch, getState) => {
            return ajax
                .post('/booking/cart/' + packagesCart.id + '/' + packagesCart.token + '/set-customer-as-lead', data, {})
                .then(
                    (onResolve: { customer: Company }) => {
                        return Promise.resolve(onResolve)
                    },
                    (onReject) => {
                        consoleLog('Fetch data error:', onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        consoleLog('Request aborted:', error.message)
                    } else {
                        consoleLog('Request error:', error)
                    }
                    return Promise.reject(error)
                })
        }
    }

    static forgottenPassword = (email: string, app_url?: string): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post('/admin/seller/forgotten-password', {
                    email: email,
                    app_url: app_url,
                })
                .then((r) => {
                    // dispatch(SessionAction.setSession(r));
                    return r
                })
        }
    }

    /***
     * @deprecated Will not be used
     */
    static resetPassword = (
        email: string,
        access_token: string,
        new_password: string,
        app_id?: string
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post('/admin/seller/reset-password', {
                    email: email,
                    password_hash: access_token,
                    new_password: new_password,
                    app_id: app_id,
                })
                .then((r) => {
                    // dispatch(SessionAction.setSession(r));
                    return r
                })
        }
    }

    static fetchSetReservationCheckoutPlan = (reservation_id: number, checkout_plan: ReservationCheckoutPlan[]) => {
        return (dispatch) => {
            return ajax
                .post('/online-venue/reservation/seller/set-reservation-checkout-plan/' + reservation_id, {
                    checkout_plan,
                })
                .then((r) => {
                    return r
                })
        }
    }

	/**
	 * @deprecated Wont be used here
	 * @param reservation_id
	 * @param upsell
	 * @param seller_id
	 */
    static setUpsellsToReservationBySeller = (
        reservation_id: number,
        upsell: SellerReservationUpsellItem[],
        seller_id: number
    ): ThunkAction<Promise<any>> => {
        return async (dispatch) => {
            try {
                const response: {
                    packagesCart: PackagesCart
                    reason?: string
                } = await ajax.post('/online-venue/reservation/seller/add_upsells/' + reservation_id, {
                    upsell: upsell,
                    seller_id: seller_id,
                })
                if (response?.packagesCart) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))

                    return Promise.resolve(response)
                } else {
                    consoleLog('setUpsellsToReservationBySeller - REJECT')
                    return Promise.reject(response.reason)
                }
            } catch (err) {
                return Promise.reject(err)
            }
        }
    }

	/**
	 * @deprecated use offersAction change_item instead to change on Offer

	 * @param reservation_id
	 * @param packagesCartItem
	 * @param type
	 * @param value
	 */
	static fetchChangeUpsellPriceAndQuantity = (
        reservation_id: number,
        packagesCartItem: PackagesCartItem,
        type: string,
        value: string | number
    ) => {
        return async (dispatch) => {
            const url =
                packagesCartItem.packageItem?.type === PackageItemItemType.Upsell
                    ? '/online-venue/reservation/seller/change_upsell/'
                    : '/online-venue/reservation/seller/change_item/'
            try {
                const response: {
                    packagesCart: PackagesCart
                    success: boolean
                } = await ajax.post(url + reservation_id, {
                    item_id: packagesCartItem.id,
                    packages_cart_id: packagesCartItem.packages_cart_id,
                    package_item_id: packagesCartItem.package_item_id,
                    type: type,
                    value: value,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('upsell_change_failed')
                }
            } catch (err: any) {
                if (err && err?.status == 401) {
                    dispatch(SellerAction.fetchSellerLogout())
                    window.location.href = '/booking/seller/login'
                }
                return Promise.reject('upsell_change_failed')
            }
        }
    }

    static fetchSellerLogout = () => {
        return async (dispatch) => {
            dispatch(BookingLoginAction.clearReservations())
            return dispatch(SessionAction.logout())
        }
    }

    /**
     * @deprecated Will not be used here
     * @param packages_cart_id
     * @param packagesCartItemVoucher
     * @param type
     * @param value
     */
    static fetchChangeVoucherPrice = (
        packages_cart_id: number,
        packagesCartItemVoucher: PackagesCartItemVoucher,
        type: string,
        value: string | number
    ) => {
        return async (dispatch) => {
            const url = '/online-venue/reservation/seller/change_voucher/'
            try {
                const response: {
                    packagesCart: PackagesCart
                    success: boolean
                } = await ajax.post(url + packages_cart_id, {
                    packages_cart_item_voucher_id: packagesCartItemVoucher.id,
                    packages_cart_id: packagesCartItemVoucher.packages_cart_id,
                    type: type,
                    value: value,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('voucher_change_failed')
                }
            } catch (err) {
                consoleLog('voucher_change_failed - reject')
                consoleLog(err)
                return Promise.reject('voucher_change_failed')
            }
        }
    }

    /**
     * @deprecated Will not be used here
     * @param packages_cart_id
     * @param type
     * @param value
     */
    static fetchSellerAddVoucher = (packages_cart_id: number, type: string, value: string | number) => {
        return async (dispatch, getState) => {
            const url = '/online-venue/reservation/seller/add_voucher/'

            try {
                const response: {
                    success: boolean
                    packagesCart: PackagesCart
                } = await ajax.post(
                    url + packages_cart_id,
                    {
                        type: type,
                        value: value,
                        api_key: SELLER_API_KEY,
                    },
                    {}
                )
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))

                    return Promise.resolve(response)
                } else {
                    return Promise.reject('voucher_change_failed')
                }
            } catch (err) {
                consoleLog('voucher_change_failed - reject')
                consoleLog(err)
                return Promise.reject('voucher_change_failed')
            }
        }
    }

    /**
     * @deprecated Will not be used
     * * @param reservation_id
     * @param packagesCartItem
     */
    static fetchDeleteUpsell = (reservation_id: number, packagesCartItem: PackagesCartItem) => {
        return async (dispatch) => {
            try {
                const response: {
                    success: boolean
                    packagesCart: PackagesCart
                } = await ajax.delete('/online-venue/reservation/seller/delete_upsell/' + reservation_id, {
                    data: {
                        item_id: packagesCartItem.id,
                        packages_cart_id: packagesCartItem.packages_cart_id,
                        package_item_id: packagesCartItem.package_item_id,
                        api_key: SELLER_API_KEY,
                    },
                })
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('upsell_delete_failed')
                }
            } catch (err) {
                consoleLog('upsell_delete_failed - reject')
                consoleLog(err)
                return Promise.reject('upsell_delete_failed')
            }
        }
    }

    /**
     * @deprecated Used in offersAction properly, remove from here
     * @param packages_cart_id
     * @param voucher_id
     */
    static fetchDeleteVoucher = (packages_cart_id: number, voucher_id: number) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    success: boolean
                    packagesCart: PackagesCart
                } = await ajax.delete('/admin/offers/delete_voucher/' + packages_cart_id, {
                    data: {
                        voucher_id: voucher_id,
                        api_key: SELLER_API_KEY,
                    },
                })
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart))
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('voucher_delete_failed')
                }
            } catch (err) {
                consoleLog('voucher_delete_failed - reject')
                consoleLog(err)
                return Promise.reject('voucher_delete_failed')
            }
        }
    }

    /**
     * @deprecated Will not be used
     * @param email
     */
    static fetchSendSellerSecurityCodeForLogin = (email: string): ((dispatch) => Promise<Driver>) => {
        return async (dispatch) => {
            try {
                const response = await ajax.post('/online-venue/reservation/seller/send-login-with-code', {
                    identifier: email,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    return Promise.resolve(response)
                } else {
                    return Promise.reject('login_not_found')
                }
            } catch (err) {
                consoleLog('fetchSendSecurityCodeForLogin - reject')
                consoleLog(err)
                return Promise.reject('login_not_found')
            }
        }
    }

    static fetchGetCompanyFromAutocomplete = (
        name: string,
        type: CompanyBranchType | null
    ): ((dispatch) => Promise<Company[]>) => {
        return async () => {
            try {
                const response = await ajax.post('/online-venue/reservation/seller/company-autocomplete', {
                    identifier: name,
                    branch_type: type,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    return Promise.resolve(response.companies)
                } else {
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchGetCompanyFromAutocomplete - reject')
                consoleLog(err)
                return Promise.reject(err)
            }
        }
    }

    static fetchGetCompanyContactFromAutocomplete = (name: string) => {
        /**
         * @deprecated Use CompaniesAction.fetchGetCompanyContactFromAutocomplete
         */
        return CompaniesAction.fetchGetCompanyContactFromAutocomplete(name)
    }

    static fetchSetReservationPlan = (
        reservation_id: number,
        plan: ReservationPlan[]
    ): ThunkAction<Promise<ReservationPlan[]>> => {
        /**
         * @deprecated Use ReservationsAction.fetchSetReservationPlan
         */
        return ReservationsAction.fetchSetReservationPlan(reservation_id, plan)
    }

    static fetchGetReservationPlan = (reservation_id: number): ThunkAction<Promise<string>> => {
        /**
         * @deprecated Use ReservationsAction.fetchGetReservationPlan
         */
        return ReservationsAction.fetchGetReservationPlan(reservation_id)
    }

    static fetchSetInvoiceNoteToReservation = (reservation_id: number, invoice_note: string | null) => {
        /**
         * @deprecated Use ReservationsAction.fetchSetInvoiceNoteToReservation
         */
        return ReservationsAction.fetchSetInvoiceNoteToReservation(reservation_id, invoice_note)
    }

    static fetchSetOfferNoteToReservation = (reservation_id: number, offer_note: string | null) => {
        /**
         * @deprecated Use ReservationsAction.fetchSetOfferNoteToReservation
         */
        return ReservationsAction.fetchSetOfferNoteToReservation(reservation_id, offer_note)
    }
    static fetchAddReservationNote = (reservation_id: number, note: string, replied_note_id?: number | null) => {
        /**
         * @deprecated Use ReservationsAction.fetchAddReservationNote
         */
        return ReservationsAction.fetchAddReservationNote(reservation_id, note, replied_note_id)
    }

    static fetchEditReservationNote = (note_id: number, note: string) => {
        /**
         * @deprecated Use ReservationsAction.fetchEditReservationNote
         */
        return ReservationsAction.fetchEditReservationNote(note_id, note)
    }

    static fetchDeleteReservationNote = (reservation_id: number, note_id: number) => {
        /**
         * @deprecated Use ReservationsAction.fetchDeleteReservationNote
         */
        return ReservationsAction.fetchDeleteReservationNote(reservation_id, note_id)
    }
}

export default SellerAction
